import type { DevCycleClient } from '@devcycle/react-client-sdk';
import posthog, { type Properties } from 'posthog-js';
import { type FeatureFlagConfig, featureFlagList } from '@/featureFlags/registry';

type DevCycleVariableReader = Pick<DevCycleClient, 'allVariables'>;

/** Registry flags owned by DevCycle (excludes PostHog-provider flags). */
export const DEVCYCLE_FEATURE_FLAGS: FeatureFlagConfig[] = featureFlagList.filter(
  flag => flag.provider === 'devcycle',
);

/** `maintenance-in-progress` -> `dvc_maintenance_in_progress` (snake_case keeps HogQL queries unquoted). */
export const toDevCyclePostHogPropertyKey = (flagKey: string): string =>
  `dvc_${flagKey.replaceAll('-', '_')}`;

/**
 * Reads served values via `allVariables()` rather than `variable()`, so syncing doesn't count as a
 * flag evaluation in DevCycle's metrics.
 */
export const buildDevCyclePostHogProperties = (client: DevCycleVariableReader): Properties => {
  const servedVariables = client.allVariables();

  return Object.fromEntries(
    DEVCYCLE_FEATURE_FLAGS.map(flag => [
      toDevCyclePostHogPropertyKey(flag.key),
      servedVariables[flag.key]?.value ?? flag.defaultValue,
    ]),
  );
};

const isPostHogUserIdentified = () => posthog.get_property('$user_state') === 'identified';

// Compared against PostHog's own super properties (not a local cache), so a `posthog.reset()`
// anywhere makes the next sync register again.
const hasUnregisteredChanges = (properties: Properties) =>
  Object.entries(properties).some(([key, value]) => posthog.get_property(key) !== value);

let lastRegisteredProperties: Properties | null = null;
// Keyed by distinct id too, so a login with unchanged flag values still writes the new person's properties.
let lastPersonPropertiesSignature: string | null = null;

/**
 * Registers `dvc_*` as PostHog super properties so they ride on every subsequent event (anonymous +
 * identified). Sets person properties only when the user is identified — never for anonymous
 * sessions (that would create person profiles). Unchanged values are not re-sent.
 */
export const syncDevCycleFlagsToPostHog = (client: DevCycleVariableReader): void => {
  const properties = buildDevCyclePostHogProperties(client);
  lastRegisteredProperties = properties;

  if (hasUnregisteredChanges(properties)) {
    posthog.register(properties);
  }

  if (!isPostHogUserIdentified()) {
    return;
  }

  const personPropertiesSignature = `${posthog.get_distinct_id()}:${JSON.stringify(properties)}`;
  if (personPropertiesSignature !== lastPersonPropertiesSignature) {
    lastPersonPropertiesSignature = personPropertiesSignature;
    posthog.setPersonProperties(properties);
  }
};

/** Re-applies the last known `dvc_*` super properties (e.g. after `posthog.reset()` on logout). */
export const reregisterDevCycleFlagsOnPostHog = (): void => {
  if (lastRegisteredProperties) {
    posthog.register(lastRegisteredProperties);
  }
};

/** Test helper — clears module state between cases. */
export const resetDevCyclePostHogSyncState = (): void => {
  lastRegisteredProperties = null;
  lastPersonPropertiesSignature = null;
};
